import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, API, fmt, fmtDate, fileUrl } from "@/lib/api";
import { DecimalInput } from "@/components/DecimalInput";
import { CustomerPicker } from "@/components/CustomerPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Save, FileText, RefreshCw, Plus, Trash2, Building2, Search,
} from "lucide-react";

export default function OfferEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [offer, setOffer] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [custOpen, setCustOpen] = useState(false);
  const [oset, setOset] = useState({ show_vat: false, vat_rate: 21 });

  const load = useCallback(() => {
    api.get(`/offers/${id}`).then((r) => setOffer(r.data));
    api.get(`/offer-settings`).then((r) => setOset(r.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!offer) return null;

  const patch = (p) => { setOffer((o) => ({ ...o, ...p })); setDirty(true); };
  const updateItem = (iid, p) => { setOffer((o) => ({ ...o, items: o.items.map((it) => it.id === iid ? { ...it, ...p } : it) })); setDirty(true); };
  const removeItem = (iid) => { setOffer((o) => ({ ...o, items: o.items.filter((it) => it.id !== iid) })); setDirty(true); };

  const addFromCalcs = (calcs) => {
    const newItems = calcs.map((c) => {
      const perKs = Math.round(((c.total || 0) / (c.portions || 1)) * 100) / 100;
      return {
        id: crypto.randomUUID(), calculation_id: c.id, name: c.name,
        unit_price: perKs, base_price: perKs,
        tiers: [{ id: crypto.randomUUID(), min_qty: 1, coefficient: 1 }],
      };
    });
    setOffer((o) => ({ ...o, items: [...o.items, ...newItems] }));
    setDirty(true);
    toast.success(`Přidáno ${newItems.length} položek`);
  };

  const addTier = (iid) => updateItem(iid, { tiers: [...(offer.items.find((i) => i.id === iid).tiers || []), { id: crypto.randomUUID(), min_qty: 1, coefficient: 1 }] });
  const updateTier = (iid, tid, p) => {
    const item = offer.items.find((i) => i.id === iid);
    updateItem(iid, { tiers: item.tiers.map((t) => t.id === tid ? { ...t, ...p } : t) });
  };
  const removeTier = (iid, tid) => {
    const item = offer.items.find((i) => i.id === iid);
    updateItem(iid, { tiers: item.tiers.filter((t) => t.id !== tid) });
  };

  const save = async () => {
    const r = await api.put(`/offers/${id}`, {
      name: offer.name, customer_id: offer.customer_id, valid_until: offer.valid_until,
      status: offer.status, note: offer.note, items: offer.items,
    });
    setOffer(r.data); setDirty(false); toast.success("Nabídka uložena");
  };
  const recompute = async () => {
    if (dirty) await save();
    const r = await api.post(`/offers/${id}/recompute`);
    setOffer(r.data); toast.success("Ceny přepočítány z aktuálních kalkulací");
  };
  const exportPdf = () => window.open(fileUrl(`/offers/${id}/export/pdf`), "_blank");

  return (
    <div className="fade-up pb-24">
      <div className="sticky top-0 z-20 backdrop-blur-xl bg-white/70 border-b border-black/5">
        <div className="px-10 py-5">
          <button onClick={() => nav("/nabidky")} data-testid="offer-back" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3">
            <ArrowLeft className="h-4 w-4" /> Zpět na nabídky
          </button>
          <div className="flex items-end justify-between gap-6">
            <div className="flex-1">
              <div className="num text-xs text-muted-foreground">{offer.number}{offer.created_by ? ` · vytvořil: ${offer.created_by}` : ""}</div>
              <input data-testid="offer-edit-name" value={offer.name} onChange={(e) => patch({ name: e.target.value })}
                className="font-display text-3xl font-medium tracking-tight bg-transparent outline-none w-full border-b border-transparent focus:border-border" />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={recompute} data-testid="offer-recompute"><RefreshCw className="h-4 w-4 mr-2" /> Přepočítat</Button>
              <Button variant="outline" size="sm" onClick={exportPdf} data-testid="offer-export-pdf"><FileText className="h-4 w-4 mr-2" /> Export PDF</Button>
              <Button size="sm" onClick={save} disabled={!dirty} className="rounded-full" data-testid="offer-save"><Save className="h-4 w-4 mr-2" /> Uložit</Button>
            </div>
          </div>
        </div>
      </div>

      <div className="px-10 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Odběratel</Label>
            <button onClick={() => setCustOpen(true)} data-testid="offer-edit-customer"
              className="mt-2 w-full flex items-center gap-2 border border-border rounded-lg px-3 py-2 text-left hover:bg-muted">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{offer.customer_name || <span className="text-muted-foreground">Vybrat…</span>}</span>
            </button>
          </div>
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Stav</Label>
            <Select value={offer.status} onValueChange={(v) => patch({ status: v })}>
              <SelectTrigger className="mt-2" data-testid="offer-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Koncept</SelectItem>
                <SelectItem value="sent">Odesláno</SelectItem>
                <SelectItem value="accepted">Přijato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="bg-white border border-border rounded-xl p-5">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Platnost do</Label>
            <Input type="date" data-testid="offer-edit-valid" value={offer.valid_until || ""} className="mt-2"
              onChange={(e) => patch({ valid_until: e.target.value })} />
          </div>
        </div>

        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-medium">Položky nabídky ({offer.items.length})</h3>
          <Button size="sm" onClick={() => setPickerOpen(true)} data-testid="offer-add-items" className="rounded-full">
            <Plus className="h-4 w-4 mr-2" /> Přidat položky z kalkulací
          </Button>
        </div>

        {offer.items.length === 0 && (
          <div className="bg-white border border-border rounded-xl px-6 py-12 text-center text-muted-foreground text-sm">
            Zatím žádné položky. Přidejte je z kalkulací tlačítkem výše.
          </div>
        )}

        {offer.items.map((it) => (
          <div key={it.id} className="bg-white border border-border rounded-xl overflow-hidden" data-testid={`offer-item-${it.id}`}>
            <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-4">
              <div className="flex-1">
                <Input value={it.name} data-testid={`offer-item-name-${it.id}`} onChange={(e) => updateItem(it.id, { name: e.target.value })}
                  className="font-display font-medium border-transparent hover:border-border focus:border-border bg-transparent max-w-md" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Cena / 1 ks</Label>
                <DecimalInput value={it.unit_price} data-testid={`offer-item-price-${it.id}`} className="h-8 w-28 text-right num"
                  onValueChange={(n) => updateItem(it.id, { unit_price: n })} />
                <span className="text-sm text-muted-foreground">Kč</span>
                <button onClick={() => removeItem(it.id)} data-testid={`offer-item-remove-${it.id}`} className="text-muted-foreground hover:text-destructive p-1.5 ml-2"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/60 hover:bg-muted/60">
                  <TableHead>Od množství (ks)</TableHead>
                  <TableHead className="w-32">Koeficient</TableHead>
                  <TableHead className="text-right">Cena/ks bez DPH</TableHead>
                  {oset.show_vat && <TableHead className="text-right">Cena/ks s DPH</TableHead>}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(it.tiers || []).map((t) => {
                  const price = (it.unit_price || 0) * (t.coefficient || 0);
                  return (
                    <TableRow key={t.id} data-testid={`offer-tier-${t.id}`}>
                      <TableCell>
                        <Input type="number" min="1" step="1" value={t.min_qty}
                          data-testid={`offer-tier-qty-${t.id}`} className="h-8 w-28 num"
                          onChange={(e) => updateTier(it.id, t.id, { min_qty: Math.max(1, parseInt(e.target.value, 10) || 1) })} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span className="text-muted-foreground">×</span>
                          <DecimalInput value={t.coefficient} data-testid={`offer-tier-coef-${t.id}`} className="h-8 w-20 num"
                            onValueChange={(n) => updateTier(it.id, t.id, { coefficient: n })} />
                        </div>
                      </TableCell>
                      <TableCell className="text-right num font-semibold">{fmt(price)} Kč</TableCell>
                      {oset.show_vat && <TableCell className="text-right num text-muted-foreground">{fmt(price * (1 + (oset.vat_rate || 0) / 100))} Kč</TableCell>}
                      <TableCell>
                        <button onClick={() => removeTier(it.id, t.id)} data-testid={`offer-tier-remove-${t.id}`} className="text-muted-foreground hover:text-destructive p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <div className="px-6 py-3 border-t border-border">
              <Button variant="ghost" size="sm" onClick={() => addTier(it.id)} data-testid={`offer-add-tier-${it.id}`} className="text-primary hover:bg-primary/10">
                <Plus className="h-4 w-4 mr-1.5" /> Přidat množstevní pásmo
              </Button>
            </div>
          </div>
        ))}

        <div className="bg-white border border-border rounded-xl p-5">
          <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Poznámka / obchodní podmínky</Label>
          <Textarea data-testid="offer-note" value={offer.note || ""} rows={3} className="mt-2"
            onChange={(e) => patch({ note: e.target.value })} placeholder="Zobrazí se v patičce PDF nabídky…" />
        </div>
      </div>

      <CalcPicker open={pickerOpen} onOpenChange={setPickerOpen} onAdd={addFromCalcs} />
      <CustomerPicker open={custOpen} onOpenChange={setCustOpen} onSelect={(c) => patch({ customer_id: c.id, customer_name: c.name })} />
    </div>
  );
}

function CalcPicker({ open, onOpenChange, onAdd }) {
  const [calcs, setCalcs] = useState([]);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState({});
  useEffect(() => { if (open) { api.get("/calculations-list").then((r) => setCalcs(r.data)); setSel({}); setSearch(""); } }, [open]);
  const filtered = calcs.filter((c) => c.name?.toLowerCase().includes(search.toLowerCase()));
  const toggle = (c) => setSel((s) => ({ ...s, [c.id]: s[c.id] ? undefined : c }));
  const count = Object.values(sel).filter(Boolean).length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Přidat položky z kalkulací</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="calcpicker-search" placeholder="Hledat kalkulaci…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" autoFocus />
        </div>
        <div className="max-h-80 overflow-auto border border-border rounded-lg divide-y divide-border">
          {filtered.map((c) => (
            <button key={c.id} data-testid={`calcpicker-item-${c.id}`} onClick={() => toggle(c)}
              className={`w-full flex items-center justify-between px-4 py-2.5 text-left ${sel[c.id] ? "bg-primary/10" : "hover:bg-muted"}`}>
              <div>
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.customer_name || "—"}</div>
              </div>
              <div className="num text-sm font-semibold">{fmt((c.total || 0) / (c.portions || 1))} Kč / ks</div>
            </button>
          ))}
          {filtered.length === 0 && <div className="px-4 py-8 text-center text-sm text-muted-foreground">Žádné kalkulace.</div>}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Zrušit</Button>
          <Button onClick={() => { onAdd(Object.values(sel).filter(Boolean)); onOpenChange(false); }} disabled={!count} data-testid="calcpicker-confirm">Přidat {count > 0 ? `(${count})` : ""}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
